import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Clock } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { SvgFlag } from '@/components/ui/SvgFlag';

interface TimeZoneInfo {
  city: string;
  timezone: string;
  countryCode: string;
}

const TIMEZONES: TimeZoneInfo[] = [
  { city: 'BD', timezone: 'Asia/Dhaka', countryCode: 'bd' },
  { city: 'NY', timezone: 'America/New_York', countryCode: 'us' },
  { city: 'London', timezone: 'Europe/London', countryCode: 'gb' },
  { city: 'Germany', timezone: 'Europe/Berlin', countryCode: 'de' },
];

export const TimezoneClock: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTimeWithErrorHandling = useCallback((time: Date, timezone: string, format: string): string => {
    try {
      return formatInTimeZone(time, timezone, format);
    } catch (error) {
      console.error(`Error formatting time for timezone ${timezone}:`, error);
      return 'Invalid time';
    }
  }, []);

  const getWorkingHoursStatus = useCallback((time: Date, timezone: string) => {
    try {
      const hour = parseInt(formatInTimeZone(time, timezone, 'H'));
      return hour >= 9 && hour < 18;
    } catch (error) {
      console.error(`Error checking working hours for timezone ${timezone}:`, error);
      return false;
    }
  }, []);

  const timezoneData = useMemo(() => {
    return TIMEZONES.map((zone) => ({
      ...zone,
      time: formatTimeWithErrorHandling(currentTime, zone.timezone, 'h:mm a'),
      date: formatTimeWithErrorHandling(currentTime, zone.timezone, 'MMM d'),
      isWorkingHours: getWorkingHoursStatus(currentTime, zone.timezone)
    }));
  }, [currentTime, formatTimeWithErrorHandling, getWorkingHoursStatus]);

  return (
    <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-primary animate-pulse" />
          <span className="text-sm font-medium text-foreground">World Clock</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {timezoneData.map((zone) => (
            <div 
              key={zone.city} 
              className={`text-center p-3 rounded-lg transition-all duration-300 hover:scale-105 ${
                zone.isWorkingHours 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 shadow-sm' 
                  : 'bg-muted/50 hover:bg-muted/70'
              }`}
              role="region"
              aria-label={`${zone.city} time: ${zone.time}`}
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <SvgFlag 
                  countryCode={zone.countryCode} 
                  className="w-4 h-3" 
                  aria-label={`${zone.city} flag`}
                />
                <span className="text-xs font-medium text-muted-foreground">
                  {zone.city}
                </span>
              </div>
              <div className="text-lg font-mono font-bold text-foreground tracking-tight">
                {zone.time}
                <span className="text-xs text-muted-foreground ml-2 font-normal">
                  {zone.date}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};